import React, { FC, useContext } from 'react';
import get from 'lodash/get';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import NodeSelector from '../NodeSelector';
import {
  Content,
  Footer,
  Row,
  ScrollRow,
  StyledTextarea,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import {
  AUTH_TYPES_OPTIONS,
  HTTP_METHODS_OPTIONS,
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import HttpNodeVariables from './components/HttpNodeVariables';

const DataDipNode: FC<NodeProps> = ({ attr, disabled, nodeList, onSelectNode, readOnly, selectedOptionIndex }) => {
  const { workingState } = useContext(FormContext);
  const isPostHttpMethod = get(workingState, `${attr}.method`) === 'POST';

  const authTypeAttr = `${attr}.auth.type`;
  const isBasicAuthType = get(workingState, authTypeAttr) === 'Basic';

  const currentNodeId = get(workingState, `${attr}.id`);

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid={`${attr}-name`}
            disabled={disabled}
            label="Name"
            description="The name you can refer to link to other nodes"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid={`${attr}-description`}
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.url`}
            data-aid={`${attr}.url`}
            description="HTTP Request allows you to interact with applications and code that live outside of Gladly"
            disabled={disabled}
            label="URL to make HTTP Request"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <Dropdown
            attr={`${attr}.method`}
            data-aid={`${attr}.method`}
            description="The desired HTTP method of your request"
            disabled={disabled}
            label="Method"
            options={HTTP_METHODS_OPTIONS}
            readOnly={readOnly}
          />
        </Row>

        {isPostHttpMethod && (
          <>
            <Row>
              <InputField
                attr={`${attr}.contentType`}
                data-aid={`${attr}.contentType`}
                description="The HTTP Content-Type of the body; required when method is POST"
                disabled={disabled}
                label="Content Type"
                readOnly={readOnly}
              />
            </Row>
            <Row>
              <StyledTextarea
                attr={`${attr}.body`}
                data-aid={`${attr}.body`}
                description="The HTTP request body; required when method is POST"
                disabled={disabled}
                label="Body"
                readOnly={readOnly}
              />
            </Row>
          </>
        )}

        <HttpNodeVariables attr={attr} disabled={disabled} readOnly={!!readOnly} />

        <Row>
          <Dropdown
            allowEmptyValues
            attr={authTypeAttr}
            data-aid={authTypeAttr}
            description="Optional if the HTTP endpoint requires authentication"
            disabled={disabled}
            label="Auth Type"
            options={AUTH_TYPES_OPTIONS}
            readOnly={readOnly}
            placeholder="None"
          />
        </Row>

        {isBasicAuthType && (
          <Row>
            <InputField
              attr={`${attr}.auth.credentialId`}
              data-aid={`${attr}.auth.credentialId`}
              label="Credential ID"
              readOnly={readOnly}
            />
          </Row>
        )}
      </Content>
      <Footer>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.nextId`}
            data-aid={`${attr}.nextId`}
            label="Next Node"
            description="The next node in the IVR"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.ERROR_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.errorNodeId`}
            data-aid={`${attr}.errorNodeId`}
            label="Error Node"
            description="If the HTTP request fails"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export default DataDipNode;
